<template>
  <v-dialog v-model="dialog" max-width="500px" persistent>
    <v-card>
      <v-card-title>
        <span class="headline">{{ isGroupMode ? 'New Group' : 'New Chat' }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-subtitle>
        <v-btn-toggle
          v-model="chatMode"
          mandatory
          color="primary"
          class="mb-2"
        >
          <v-btn value="direct" small>
            <v-icon left>mdi-account</v-icon>
            Direct Chat
          </v-btn>
          <v-btn value="group" small>
            <v-icon left>mdi-account-group</v-icon>
            Group Chat
          </v-btn>
        </v-btn-toggle>
      </v-card-subtitle>

      <v-card-text>
        <!-- Group name input for group mode -->
        <v-text-field
          v-if="isGroupMode"
          v-model="groupName"
          prepend-inner-icon="mdi-account-group"
          placeholder="Enter group name..."
          outlined
          dense
          hide-details
          class="mb-4"
        ></v-text-field>

        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          :placeholder="isGroupMode ? 'Search users to add to group...' : 'Search users...'"
          outlined
          dense
          hide-details
          @input="searchUsers"
          class="mb-4"
        ></v-text-field>

        <div v-if="loading" class="text-center pa-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <div v-else-if="users.length === 0 && searchQuery" class="text-center pa-4">
          <v-icon size="48" color="grey lighten-2">mdi-account-search</v-icon>
          <p class="mt-2 grey--text">No users found</p>
        </div>

        <div v-else-if="users.length === 0" class="text-center pa-4">
          <v-icon size="48" color="grey lighten-2">mdi-account-group</v-icon>
          <p class="mt-2 grey--text">{{ isGroupMode ? 'Search for users to add to group' : 'Search for users to start a conversation' }}</p>
        </div>

        <!-- Selected users for group mode -->
        <div v-if="isGroupMode && selectedUsers.length > 0" class="mb-4">
          <v-subheader>Selected Users ({{ selectedUsers.length }})</v-subheader>
          <v-chip-group>
            <v-chip
              v-for="user in selectedUsers"
              :key="user._id"
              close
              @click:close="removeSelectedUser(user)"
              color="primary"
              outlined
            >
              <v-avatar left>
                <v-img
                  v-if="user.profileimg"
                  :src="user.profileimg"
                ></v-img>
                <v-icon v-else>mdi-account</v-icon>
              </v-avatar>
              {{ user.Name }}
            </v-chip>
          </v-chip-group>
        </div>

        <v-list v-if="users.length > 0">
          <v-list-item
            v-for="user in users"
            :key="user._id"
            @click="selectUser(user)"
            class="user-item"
            :class="{ 'selected-user': isSelectedUser(user) }"
          >
            <v-list-item-avatar>
              <v-img
                v-if="user.profileimg"
                :src="user.profileimg"
              ></v-img>
              <v-avatar v-else color="primary">
                <v-icon>mdi-account</v-icon>
              </v-avatar>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title>{{ user.Name }}</v-list-item-title>
              <v-list-item-subtitle>{{ user.Email }}</v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <v-icon
                v-if="isGroupMode && isSelectedUser(user)"
                color="primary"
              >
                mdi-check
              </v-icon>
              <v-icon
                v-else-if="user.isOnline"
                color="green"
                small
              >
                mdi-circle
              </v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn
          v-if="isGroupMode"
          color="primary"
          @click="createGroup"
          :disabled="!canCreateGroup"
        >
          Create Group
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import { debounce } from 'lodash';

const url = require("@/client_config/config").url;

export default {
  name: 'NewChatDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: '',
      users: [],
      loading: false,
      chatMode: 'direct',
      groupName: '',
      selectedUsers: []
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    isGroupMode() {
      return this.chatMode === 'group';
    },
    canCreateGroup() {
      return this.groupName.trim() && this.selectedUsers.length > 0;
    }
  },
  watch: {
    dialog(newVal) {
      console.log('Dialog value changed:', newVal);
      if (newVal) {
        this.searchQuery = '';
        this.users = [];
        this.chatMode = 'direct';
        this.groupName = '';
        this.selectedUsers = [];
      }
    }
  },
  methods: {
    searchUsers: debounce(async function() {
      if (!this.searchQuery || this.searchQuery.length < 2) {
        this.users = [];
        return;
      }

      this.loading = true;
      try {
        console.log('Searching users with query:', this.searchQuery);
        const response = await axios.get(`${url}/chat/search/users`, {
          params: { query: this.searchQuery }
        });
        console.log('Search response:', response.data);
        this.users = response.data.users;
      } catch (error) {
        console.error('Error searching users:', error);
        console.error('Error details:', error.response?.data);
        this.users = [];
      } finally {
        this.loading = false;
      }
    }, 300),

    selectUser(user) {
      console.log('Selected user:', user);
      
      if (this.isGroupMode) {
        // For group mode, add/remove user from selection
        if (this.isSelectedUser(user)) {
          this.removeSelectedUser(user);
        } else {
          this.selectedUsers.push(user);
        }
      } else {
        // For direct mode, create conversation immediately
        const conversation = {
          type: 'direct',
          id: user._id,
          user: user,
          lastMessage: null,
          unreadCount: 0
        };
        
        console.log('Created conversation:', conversation);
        this.$emit('chat-created', conversation);
        this.close();
      }
    },

    isSelectedUser(user) {
      return this.selectedUsers.some(selectedUser => selectedUser._id === user._id);
    },

    removeSelectedUser(user) {
      this.selectedUsers = this.selectedUsers.filter(selectedUser => selectedUser._id !== user._id);
    },

    async createGroup() {
      if (!this.canCreateGroup) return;

      try {
        const groupData = {
          name: this.groupName.trim(),
          memberIds: this.selectedUsers.map(user => user._id)
        };

        const response = await axios.post(`${url}/chat/groups`, groupData);
        const group = response.data.group;

        const conversation = {
          type: 'group',
          id: group._id,
          group: group,
          lastMessage: null,
          unreadCount: 0
        };

        console.log('Created group conversation:', conversation);
        this.$emit('chat-created', conversation);
        this.close();
      } catch (error) {
        console.error('Error creating group:', error);
        // You might want to show an error message to the user here
      }
    },

    close() {
      console.log('Closing dialog...');
      this.dialog = false;
      this.searchQuery = '';
      this.users = [];
    }
  }
};
</script>

<style scoped>
.user-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.user-item:hover {
  background-color: #f5f5f5;
}

.selected-user {
  background-color: #e3f2fd !important;
}

.selected-user:hover {
  background-color: #bbdefb !important;
}

/* Ensure dialog appears on top */
:deep(.v-dialog) {
  z-index: 9999 !important;
}

:deep(.v-dialog__content) {
  z-index: 9999 !important;
}

:deep(.v-overlay) {
  z-index: 9998 !important;
}

/* Mobile dialog improvements */
@media (max-width: 960px) {
  :deep(.v-dialog) {
    margin: 16px !important;
  }
  
  :deep(.v-card) {
    border-radius: 12px !important;
  }
}


</style> 